/**
 * โพล์ตรวจ session กับ user-auth เป็นระยะ
 * เมื่อล็อกอินจาก browser/อุปกรณ์อื่น backend จะตั้ง currentSessionIssuedAt ใหม่
 * การเรียก user-auth (เช่น GET /profile) จะได้ 401 SESSION_REPLACED
 * ทำให้ browser เก่าได้แจ้งเตือนและ logout อัตโนมัติ
 *
 * ปรับปรุง:
 * - หยุด poll เมื่อ tab ไม่ active (Page Visibility API) — ลด request ที่ไม่จำเป็น
 * - interval 60s (เดิม 30s) เพียงพอสำหรับตรวจ session
 * - throttle + backoff ตอน 429
 */
import { useEffect, useRef } from 'react'
import * as authService from '../services/authService'
import { handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

/** ระยะเวลา poll ปกติ (tab active) */
const POLL_INTERVAL_MS = 60_000
/** ขั้นต่ำห่างกันระหว่าง request (ป้องกัน multi-instance ยิงซ้อน) */
const MIN_PROFILE_INTERVAL_MS = 55_000
/** เมื่อได้ 429 ไม่ยิงซ้ำก่อนครบช่วงนี้ */
const BACKOFF_AFTER_429_MS = 120_000
const PROFILE_URL = '/api/v1/user-auth/profile'

// Module-level throttle — ใช้ร่วมกันทุก instance ของ hook นี้
let lastProfileCheckAt = 0
let backoffUntil = 0

export function useSessionReplacedPoll(enabled: boolean) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const checkSession = async () => {
      // หยุดเมื่อ tab ไม่ active — ไม่ต้อง ping server เมื่อผู้ใช้ไม่ได้ดูหน้าจอ
      if (document.visibilityState === 'hidden') return

      const token = authService.getAccessToken()
      const tenantId = getRuntimeTenantIdSync() || ''
      if (!token) return

      const now = Date.now()
      if (now < backoffUntil) return
      if (now - lastProfileCheckAt < MIN_PROFILE_INTERVAL_MS) return
      lastProfileCheckAt = now

      try {
        const res = await fetch(withApiBaseUrl(PROFILE_URL), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': tenantId,
          },
          credentials: 'include',
        })
        if (res.status === 429) {
          backoffUntil = Date.now() + BACKOFF_AFTER_429_MS
          return
        }
        if (res.status === 401) {
          const data = (await res.json().catch(() => ({}))) as { code?: string }
          if (data?.code === 'SESSION_REPLACED') {
            await handleSessionReplaced()
          }
        }
      } catch {
        // ignore network errors; next poll will retry
      }
    }

    // เรียกครั้งแรกเมื่อ tab กลับมา active หลังจากซ่อนอยู่
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkSession()
    }

    checkSession()
    intervalRef.current = setInterval(checkSession, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled])
}
